import {pluralize, underscore} from "inflection";
import * as R from "ramda";
import { AnyAction, Dispatch } from "redux";

import { fetchForReduxPromiseMiddleware } from "../src/fetch_for_redux";

import {
  handleDelete,
} from "../src/edit_actions/delete";

import {
  handleTextInsert,
  handleDataTransferInsert,
} from "../src/edit_actions/insert";

import {
  handleNewLineInsert,
} from "../src/edit_actions/new_line";

import {
  handleNewListInsert,
} from "../src/edit_actions/new_list";

import {
  handleNewMediaInsert,
} from "../src/edit_actions/new_media";

import {
  handleStyle,
} from "../src/edit_actions/style";

export const READ = "item/READ";
export const CREATE = "item/CREATE";
export const UPDATE = "item/UPDATE";
export const DESTROY = "item/DESTROY";

export const CREATE_FULFILLED = "item/CREATE_FULFILLED";
export const CREATE_PENDING = "item/CREATE_PENDING";
export const CREATE_REJECTED = "item/CREATE_REJECTED";
export const DESTROY_FULFILLED = "item/DESTROY_FULFILLED";
export const DESTROY_PENDING = "item/DESTROY_PENDING";
export const DESTROY_REJECTED = "item/DESTROY_REJECTED";
export const READ_FULFILLED = "item/READ_FULFILLED";
export const READ_PENDING = "item/READ_PENDING";
export const READ_REJECTED = "item/READ_REJECTED";
export const RESET_EDIT_DATA = "item/RESET_EDIT_DATA";
export const RESET_LOCAL_DATA = "item/RESET_LOCAL_DATA";
export const SEED = "item/SEED";
export const SET_EDIT_DATA_FIELDS = "item/SET_EDIT_DATA_FIELDS";
export const SET_LOCAL_DATA_FIELDS = "item/SET_LOCAL_DATA_FIELDS";
export const UPDATE_FULFILLED = "item/UPDATE_FULFILLED";
export const UPDATE_PENDING = "item/UPDATE_PENDING";
export const UPDATE_REJECTED = "item/UPDATE_REJECTED";

export const READ_PAGE = "item/list/READ_PAGE";
export const READ_PAGE_PENDING = "item/list/READ_PAGE_PENDING";
export const READ_PAGE_FULFILLED = "item/list/READ_PAGE_FULFILLED";
export const READ_PAGE_REJECTED = "item/list/READ_PAGE_REJECTED";
export const RELOAD_LIST = "item/list/RELOAD_LIST";
export const RELOAD_LIST_PENDING = "item/list/RELOAD_LIST_PENDING";
export const RELOAD_LIST_FULFILLED = "item/list/RELOAD_LIST_FULFILLED";
export const RELOAD_LIST_REJECTED = "item/list/RELOAD_LIST_REJECTED";

export const RESET_PAGES = "item/list/RESET_PAGES";

export const ADD_FILTER = "item/list/ADD_FILTER";
export const REMOVE_FILTER = "item/list/REMOVE_FILTER";

export const SET_SEARCH = "item/list/SET_SEARCH";
export const RESET_SEARCH = "item/list/RESET_SEARCH";

export const SET_SORT = "item/list/SET_SORT";
export const RESET_SORT = "item/list/RESET_SORT";

export const SET_SELECTED = "item/list/SET_SELECTED";
export const RESET_SELECTED = "item/list/RESET_SELECTED";
export const ADD_SELECTED = "item/list/ADD_SELECTED";
export const REMOVE_SELECTED = "item/list/REMOVE_SELECTED";

export const SET_ITEMS_PER_PAGE = "item/list/SET_ITEMS_PER_PAGE";

const DEFAULT_ITEMS_AND_LISTS_STATE = {
  items: {},
  lists: {},
};

export function itemsAndListsReducer<Item extends {id: number}, ItemLocalData extends object>(itemType: ItemType, state: ItemsAndListsState<Item, ItemLocalData>=DEFAULT_ITEMS_AND_LISTS_STATE, action: ItemAndListAction<Item, ItemLocalData>):  ItemsAndListsState<Item, ItemLocalData> {
  if (!action || !action.meta || action.meta.itemType != itemType) {
    return state;
  }

  switch (action.type) {
    case READ_PENDING:
    case CREATE_FULFILLED:
    case DESTROY_FULFILLED:
    case SEED:
    case CREATE_PENDING:
    case CREATE_REJECTED:
    case READ_FULFILLED:
    case READ_REJECTED:
    case UPDATE_PENDING:
    case UPDATE_FULFILLED:
    case UPDATE_REJECTED:
    case DESTROY_PENDING:
    case DESTROY_REJECTED:
    case SET_EDIT_DATA_FIELDS:
    case RESET_EDIT_DATA:
    case SET_LOCAL_DATA_FIELDS:
    case RESET_LOCAL_DATA:
      return {
        ...state,
        items: itemsReducer(itemType, state.items, action),
      };
    case READ_PAGE_PENDING:
    case READ_PAGE_REJECTED:
    case RELOAD_LIST_PENDING:
    case RELOAD_LIST_REJECTED:
    case RESET_PAGES:
    case ADD_FILTER:
    case REMOVE_FILTER:
    case SET_SEARCH:
    case RESET_SEARCH:
    case SET_SORT:
    case RESET_SORT:
    case SET_SELECTED:
    case RESET_SELECTED:
    case ADD_SELECTED:
    case REMOVE_SELECTED:
    case SET_ITEMS_PER_PAGE:
      return {
        ...state,
        lists: listsReducer(itemType, state.lists, action),
      };
    case READ_PAGE_FULFILLED:
    case RELOAD_LIST_FULFILLED:
      const newItemsState = (action.payload.data as Item[]).reduce((stateItems, listItem) => {
        const readFulfilledAction: ItemFulfilledAction<Item> = {
          type: READ_FULFILLED,
          meta: {
            id: listItem.id,
            itemType: itemType,
          },
          payload: {
            data: listItem,
          },
        };

        return itemsReducer(
          itemType,
          stateItems,
          readFulfilledAction,
        );
      }, state.items);

      return {
        ...state,
        items: newItemsState,
        lists: listsReducer(itemType, state.lists, action),
      };
    default:
      return state;
  }
}

const DEFAULT_ITEMS_STATE = {};

function itemsReducer<Item extends {id: number}, ItemLocalData extends object>(itemType: ItemType, state: ItemsState<Item, ItemLocalData>=DEFAULT_ITEMS_STATE, action: ItemAction<Item, ItemLocalData>):  ItemsState<Item, ItemLocalData> {
  if (!action || !action.meta || action.meta.itemType != itemType) {
    return state;
  }

  switch (action.type) {
    case READ_PENDING:
    case READ_FULFILLED:
      return {...state,
        [action.meta.id]: itemReducer<Item, ItemLocalData>(state[action.meta.id], action),
      };
    case CREATE_FULFILLED:
      return {...R.dissoc(action.meta.id.toString(), state),
        [action.payload.data.id]: itemReducer<Item, ItemLocalData>(state[action.meta.id], action),
      };
    case DESTROY_FULFILLED:
      return R.omit([action.meta.id.toString()], state);
    case SEED:
    case CREATE_PENDING:
    case CREATE_REJECTED:
    case READ_REJECTED:
    case UPDATE_PENDING:
    case UPDATE_FULFILLED:
    case UPDATE_REJECTED:
    case DESTROY_PENDING:
    case DESTROY_REJECTED:
    case SET_EDIT_DATA_FIELDS:
    case RESET_EDIT_DATA:
    case SET_LOCAL_DATA_FIELDS:
    case RESET_LOCAL_DATA:
      if (state[action.meta.id]) {
        return {...state,
          [action.meta.id]: itemReducer<Item, ItemLocalData>(state[action.meta.id], action),
        };
      } else {
        return state;
      }
    default:
      return state;
  }
}

const DEFAULT_EDIT_DATA = {};
const DEFAULT_LOCAL_DATA = {};
const DEFAULT_ITEM_STATE = {
  editData: DEFAULT_EDIT_DATA,
  localData: DEFAULT_LOCAL_DATA,
  isSyncing: false,
};

function itemReducer<Item extends {id: number}, ItemLocalData extends object>(state: ItemState<Item, ItemLocalData>=DEFAULT_ITEM_STATE, action: ItemAction<Item, ItemLocalData>): ItemState<Item, ItemLocalData> {
  switch (action.type) {
    case SEED:
      return {...state,
        savedData: action.payload.data,
      };
    case CREATE_PENDING:
    case READ_PENDING:
    case UPDATE_PENDING:
    case DESTROY_PENDING:
      return {...state,
        isSyncing: true,
      };
    case CREATE_REJECTED:
    case READ_REJECTED:
    case UPDATE_REJECTED:
    case DESTROY_REJECTED:
      return {...state,
        isSyncing: false,
        syncError: action.payload.message,
      };
    case CREATE_FULFILLED:
    case UPDATE_FULFILLED:
      return {...state,
        isSyncing: false,
        lastSyncedAt: Date.now(),
        syncError: undefined,
        editData: DEFAULT_EDIT_DATA,
        savedData: action.payload.data,
      };
    case READ_FULFILLED:
      return {...state,
        isSyncing: false,
        lastSyncedAt: Date.now(),
        syncError: undefined,
        savedData: action.payload.data,
      };
    case SET_EDIT_DATA_FIELDS:
      return {...state,
        editData: {...state.editData,
          ...action.payload.fields,
        },
      };
    case RESET_EDIT_DATA:
      return {...state,
        editData: DEFAULT_EDIT_DATA,
      };
    case SET_LOCAL_DATA_FIELDS:
      return {...state,
        localData: {...state.localData,
          ...action.payload.fields,
        },
      };
    case RESET_LOCAL_DATA:
      return {...state,
        localData: DEFAULT_LOCAL_DATA,
      };
    default:
      return state;
  }
}


const DEFAULT_LISTS_STATE = {};

function listsReducer<Item extends {id: number}>(itemType: ItemType, state: ItemListsState<Item>=DEFAULT_LISTS_STATE, action: ItemListAction<Item>): ItemListsState<Item> {
  if (!action || !action.meta || action.meta.itemType != itemType) {
    return state;
  }

  switch (action.type) {
    case READ_PAGE_PENDING:
    case READ_PAGE_REJECTED:
    case RELOAD_LIST_PENDING:
    case RELOAD_LIST_REJECTED:
    case RESET_PAGES:
    case ADD_FILTER:
    case REMOVE_FILTER:
    case SET_SEARCH:
    case RESET_SEARCH:
    case SET_SORT:
    case RESET_SORT:
    case SET_SELECTED:
    case RESET_SELECTED:
    case ADD_SELECTED:
    case REMOVE_SELECTED:
    case SET_ITEMS_PER_PAGE:
    case READ_PAGE_FULFILLED:
    case RELOAD_LIST_FULFILLED:
      return {
        ...state,
        [action.meta.listID]: listReducer(state[action.meta.listID], action),
      };
    default:
      return state;
  }

}

// TODO: POSSIBLY REVISE
const DEFAULT_LIST_STATE = {
  isSyncing: false,
  page: 0,
  perPage: 24,
  selected: [],
};

function listReducer<Item extends {id: number}>(state: ItemListState<Item>=DEFAULT_LIST_STATE, action: ItemListAction<Item>): ItemListState<Item> {
  switch (action.type) {
    case READ_PAGE_PENDING:
    case RELOAD_LIST_PENDING:
      return {...state,
        isSyncing: true,
      };
    case READ_PAGE_FULFILLED:
      return {...state,
        isSyncing: false,
        lastSyncedAt: Date.now(),
        syncError: undefined,
        listData: ("listData" in state ? state.listData : []).concat((action.payload.data as Item[]).map((item) => item.id)),
        page: state.page + 1,
        lastPageLength: action.payload.data.length,
      };
    case RELOAD_LIST_FULFILLED:
      return {...state,
        isSyncing: false,
        lastSyncedAt: Date.now(),
        syncError: undefined,
        listData: (action.payload.data as Item[]).map((item) => item.id),
        page: state.page,
        lastPageLength: Math.max(0, action.payload.data.length - (state.page * state.perPage)),
      };
    case READ_PAGE_REJECTED:
    case RELOAD_LIST_REJECTED:
      return {...state,
        isSyncing: false,
        syncError: action.payload.message,
      };
    case RESET_PAGES:
      return {...state,
        isSyncing: false,
        lastSyncedAt: undefined,
        syncError: undefined,
        listData: undefined,
        page: 0,
        lastPageLength: undefined,
      };
    case ADD_FILTER:
      return {...state,
        activeFilters: {...state.activeFilters,
          [action.payload.filterID]: action.payload.filter,
        },
      };
    case REMOVE_FILTER:
      return {...state,
        activeFilters: state.activeFilters ? R.dissoc(action.payload.filterID, state.activeFilters) : {},
      };
    case SET_SEARCH:
      return {...state,
        search: action.payload.search,
      };
    case RESET_SEARCH:
      return R.omit(["search"], state);
    case SET_SORT:
      return {...state,
        sortBy: action.payload.sortBy,
        sortOrder: action.payload.sortOrder,
      };
    case RESET_SORT:
      return R.omit(["sortBy", "sortOrder"], state);
    case SET_SELECTED:
      return {...state,
        selected: action.payload.selected,
      };
    case RESET_SELECTED:
      return {...state,
        selected: [],
      };
    case ADD_SELECTED:
      return {...state,
        selected: R.union(state.selected, action.payload.addSelected),
      };
    case REMOVE_SELECTED:
      return {...state,
        selected: R.difference(state.selected, action.payload.removeSelected),
      };
    case SET_ITEMS_PER_PAGE:
      return {...state,
        perPage: action.payload.perPage,
      };
    default:
      return state;
  }
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

export function generateItemsReducer<Item extends {id: number}, ItemLocalData extends object>(itemType: ItemType) {
  return (state:ItemsAndListsState<Item, ItemLocalData>, action: ItemAction<Item, ItemLocalData>) => itemsAndListsReducer(itemType, state, action);
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

function rawItemsAndListsFromStore<Item extends {id: number}, ItemLocalData extends object>(store: GlobalStore, itemType: ItemType): ItemsAndListsState<Item, ItemLocalData> {
  const storeReducerName = pluralize(itemType) as keyof GlobalStore;
  const rawStoreItemAndLists = store[storeReducerName];
  return R.isEmpty(rawStoreItemAndLists) ? DEFAULT_ITEMS_AND_LISTS_STATE : rawStoreItemAndLists as ItemsAndListsState<Item, ItemLocalData>;
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

function rawItemFromStore<Item extends {id: number}, ItemLocalData extends object>(store: GlobalStore, itemType: ItemType, id: ItemId): ItemState<Item, ItemLocalData> {
  const itemsAndListsState = rawItemsAndListsFromStore<Item, ItemLocalData>(store, itemType);
  return itemsAndListsState.items[id] || {...DEFAULT_ITEM_STATE};
}

function defaultDataMergeFunction<Item extends {id: number}, ItemLocalData extends object>(rawItem: ItemStateLoaded<Item, ItemLocalData>) : Item {
  return {...rawItem.savedData, ...rawItem.editData};
}

export function itemFromStore<Item extends {id: number}, ItemLocalData extends object>(store: GlobalStore, itemType: ItemType, id: ItemId, mergeFunction: (item: ItemStateLoaded<Item, ItemLocalData>) => Item = defaultDataMergeFunction): ItemFromStore<Item, ItemLocalData>  {
  const rawItem = rawItemFromStore<Item, ItemLocalData>(store, itemType, id);

  const baseItem = {
    id: id,
    "_editData": rawItem?.editData || {},
    "_localData": rawItem?.localData || {},
    isSyncing: rawItem?.isSyncing || false,
    syncError: rawItem?.syncError,
  };

  if (!rawItem) {
    return baseItem;
  }

  if ("savedData" in rawItem) {
    return {
      ...baseItem,
      ...mergeFunction(rawItem),
      "_savedData": rawItem.savedData,
      lastSyncedAt: rawItem.lastSyncedAt,
    };
  } else {
    return {
      ...baseItem,
      ...rawItem.editData,
    };
  }
}

type GenerateExtraActionsFunction<Item extends {id: number}, ItemLocalData extends object, ExtraActions extends object> = (
  dispatch: Dispatch,
  actions: ReturnType<typeof generateItemActions<Item, ItemLocalData>>,
  getItemFromStore: (store: GlobalStore) => (id: ItemId) => ItemFromStore<Item, ItemLocalData>,
  id: ItemId
) => ExtraActions;

export const generateItemAndActionsFromStore = <Item extends {id: number}, ItemLocalData extends object, ExtraActions extends object>(
  itemType: ItemType,
  endpoint: string,
  generateExtraActions: GenerateExtraActionsFunction<Item, ItemLocalData, ExtraActions>,
  mergeFunction?: (item: ItemStateLoaded<Item, ItemLocalData>) => Item,
) => (
  dispatch: Dispatch,
  store: GlobalStore,
) => (
  id: ItemId,
): ItemAndActionsFromStore<Item, ItemLocalData, ExtraActions> => {
  const getItemFromStore = (store: GlobalStore) => (id: ItemId) => itemFromStore<Item, ItemLocalData>(store, itemType, id, mergeFunction);

  const item = getItemFromStore(store)(id);

  const actions = generateItemActions<Item, ItemLocalData>(dispatch, itemType, endpoint, id);
  const extraActions = generateExtraActions(dispatch, actions, getItemFromStore, id);

  return {
    ...item,
    _actions: {
      ...actions,
      ...extraActions,
    },
  };
};

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

function rawListFromStore<Item extends {id: number}, ItemLocalData extends object>(store: GlobalStore, itemType: ItemType, listID: string): ItemListState<Item> {
  const itemsAndListsState = rawItemsAndListsFromStore<Item, ItemLocalData>(store, itemType);
  return itemsAndListsState.lists[listID];
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

function defaultOutTransformer<Item>(data: Item) { return data; }

// function createItemWithData(itemType, endpoint, tempId, data, outTransformer=defaultOutTransformer) {
//   return {
//     type: CREATE,
//     meta: {
//       itemType,
//       tempId,
//     },
//     payload: fetchForReduxPromiseMiddleware(`/${endpoint}`, {method: "POST", body: JSON.stringify(outTransformer(data))}),
//   }
// }

// export function createItem(itemType, endpoint, tempId, outTransformer=defaultOutTransformer) {
//   return (dispatch, getState) => {
//     let state = getState()
//     let item = state[pluralize(itemType)][tempId]
//     let editData = item.editData

//     dispatch(createItemWithData(itemType, endpoint, tempId, editData, outTransformer=defaultOutTransformer))
//   }
// }

export function readItem(itemType: ItemType, endpoint: string, id: string) {
  return {
    type: READ,
    meta: {
      itemType,
      id,
    },
    payload: fetchForReduxPromiseMiddleware(`/${endpoint}${id || ""}`),
  };
}

export function updateItemWithData<Item extends {id: number}>(itemType: ItemType, endpoint: string, id: string, data: Item, outTransformer=defaultOutTransformer) {
  return {
    type: UPDATE,
    meta: {
      itemType,
      id,
    },
    payload: fetchForReduxPromiseMiddleware(`/${endpoint}${id || ""}`, {method: "PUT", body: JSON.stringify({[underscore(itemType)]: outTransformer(data)})}),
  };
}

export function getTypeStuffTest<K extends keyof GlobalStore>(state: GlobalStore, itemType: K) {
  return state[itemType];
}

export function updateItem<Item extends {id: number}>(itemType: ItemType, endpoint: string, id: string, outTransformer=defaultOutTransformer) {
  return (dispatch: Dispatch, getState: () => GlobalStore) => {
    const state = getState();
    const item = state[pluralize(itemType) as keyof GlobalStore].items[id];
    const editData = item.editData;

    dispatch(updateItemWithData<Item>(itemType, endpoint, id, editData, outTransformer=defaultOutTransformer));
  };
}

export function destroyItem(itemType: ItemType, endpoint: string, id: string) {
  return {
    type: DESTROY,
    meta: {
      itemType: itemType,
      id: id,
    },
    payload: fetchForReduxPromiseMiddleware(`/${endpoint}${id || ""}`, {method: "DELETE"}),
  };
}

export function generateItemActions<Item extends {id: number}, ItemLocalData extends object>(dispatch: Dispatch, itemType: ItemType, endpoint: string, id: ItemId): ItemActions<Item, ItemLocalData> {
  return {
    seed: (data: Item) => dispatch(({
      type: SET_LOCAL_DATA_FIELDS,
      meta: {itemType, id: id.toString()},
      payload: {data},
    })),
    read: () => dispatch(readItem(itemType, endpoint, id.toString())),
    update: () => dispatch(updateItem(itemType, endpoint, id.toString()) as unknown as AnyAction),
    destroy: () => dispatch(destroyItem(itemType, endpoint, id.toString())),
    setEditFields: (fields: Partial<Item>) => dispatch(({
      type: SET_EDIT_DATA_FIELDS,
      meta: {itemType, id},
      payload: {fields},
    })),
    resetEditFields: () => dispatch(({
      type: RESET_EDIT_DATA,
      meta: {itemType, id},
    })),
    setLocalFields: (fields: Partial<ItemLocalData>) => dispatch(({
      type: SET_LOCAL_DATA_FIELDS,
      meta: {itemType, id},
      payload: {fields},
    })),
    resetLocalFields: () => dispatch(({
      type: RESET_LOCAL_DATA,
      meta: {itemType, id},
    })),
  };
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface EditLocalData<DocContent> {
  undoQueue: UndoRedoQueue<DocContent>;
  redoQueue: UndoRedoQueue<DocContent>;
  currentlyEditing: CurrentlyEditing;
}

export function generateGenerateEditActions<Item extends {id: number}, ItemLocalData extends EditLocalData<DocContent>, DocContent extends PossibleContent>(
  getContent: (item: ItemFromStore<Item, ItemLocalData>) => DocContent | undefined,
  setContent: (content: DocContent) => Partial<Item>,
) {
  return (
    dispatch: Dispatch,
    actions: ReturnType<typeof generateItemActions<Item, ItemLocalData>>,
    getItemFromStore: (store: GlobalStore) => (id: ItemId) => ItemFromStore<Item, ItemLocalData>,
    id: ItemId,
  ) => generateEditActions<Item, ItemLocalData, DocContent>(
    dispatch,
    getItemFromStore,
    actions,
    getContent,
    setContent,
    id,
  );
}

export function generateEditActions<Item extends {id: number}, ItemLocalData extends EditLocalData<DocContent>, DocContent extends PossibleContent>(
  dispatch: Dispatch,
  getItemFromStore: (store: GlobalStore) => (id: ItemId) => ItemFromStore<Item, ItemLocalData>,
  actions: ReturnType<typeof generateItemActions<Item, ItemLocalData>>,
  getContent: (item: ItemFromStore<Item, ItemLocalData>) => DocContent | undefined,
  setContent: (content: DocContent) => Partial<Item>,
  id: ItemId,
): ItemEditActions<DocContent> {
  const addToUndo = (newContent: DocContent, newDocSelection?: CurrentlyEditing) => {
    dispatch(
      ((dispatch: Dispatch, getState: () => GlobalStore) => {
        const item = getItemFromStore(getState())(id);

        const {
          undoQueue = [],
        } = item._localData;

        const newUndo = ([[newContent, newDocSelection]] as UndoRedoQueue<DocContent>).concat(undoQueue);

        actions.setLocalFields({
          currentlyEditing: undefined,
          undoQueue: newUndo,
          redoQueue: [] as UndoRedoQueue<DocContent>,
        } as Partial<ItemLocalData>);
      }) as unknown as AnyAction,
    );
  };

  const redo = () => {
    dispatch(
      ((dispatch: Dispatch, getState: () => GlobalStore) => {
        const item = getItemFromStore(getState())(id);

        const {
          currentlyEditing,
          undoQueue = [],
          redoQueue = [],
        } = item._localData;

        const content = getContent(item);

        if (content && redoQueue.length > 0) {
          const [newContent, newDocSelection] = redoQueue[0];

          const newUndo = ([[content, currentlyEditing]] as UndoRedoQueue<DocContent>).concat(undoQueue);
          const newRedo = redoQueue.slice(1);

          actions.setEditFields(setContent(newContent));
          actions.setLocalFields({
            undoQueue: newUndo,
            redoQueue: newRedo,
            currentlyEditing: newDocSelection,
          } as Partial<ItemLocalData>);
        }
      }) as unknown as AnyAction,
    );
  };

  const undo = () => {
    dispatch(
      ((dispatch: Dispatch, getState: () => GlobalStore) => {
        const item = getItemFromStore(getState())(id);

        const {
          currentlyEditing,
          undoQueue = [],
          redoQueue = [],
        } = item._localData;

        const content = getContent(item);

        if (content && undoQueue.length > 0) {
          const [newContent, newDocSelection] = undoQueue[0];

          const newUndo = undoQueue.slice(1);
          const newRedo = ([[content, currentlyEditing]] as UndoRedoQueue<DocContent>).concat(redoQueue);

          actions.setEditFields(setContent(newContent));
          actions.setLocalFields({
            undoQueue: newUndo,
            redoQueue: newRedo,
            currentlyEditing: newDocSelection,
          } as Partial<ItemLocalData>);
        }
      }) as unknown as AnyAction,
    );
  };

  const modifyContent = (modifyFunction: ModifyFunction<DocContent>) => {
    dispatch(
      ((dispatch: Dispatch, getState: () => GlobalStore) => {
        const item = getItemFromStore(getState())(id);

        const {
          currentlyEditing,
        } = item._localData;

        const content = getContent(item);

        if (!content) { return; }

        addToUndo(content, currentlyEditing);
        actions.setEditFields(setContent(modifyFunction(content, currentlyEditing)));
      }) as unknown as AnyAction,
    );
  };

  const resetUndoRedo = () => {
    actions.setLocalFields({
      undoQueue: [] as UndoRedoQueue<DocContent>,
      redoQueue: [] as UndoRedoQueue<DocContent>,
    } as Partial<ItemLocalData>);
  };

  const setCurrentlyEditing = (currentlyEditing: CurrentlyEditing | undefined) => actions.setLocalFields({currentlyEditing} as Partial<ItemLocalData>);

  return {
    modifyEditJSON: modifyContent,
    // addToUndo,
    redo,
    undo,
    resetUndoRedo,
    setCurrentlyEditing: setCurrentlyEditing,
    handleTextInsert: (data: string, selectionOverride?: DocSelection) => {
      return handleTextInsert(
        data,
        modifyContent,
        setCurrentlyEditing,
        selectionOverride,
      );
    },
    handleDataTransferInsert: (dataTransfer: DataTransfer, selectionOverride?: DocSelection) => {
      return handleDataTransferInsert(
        dataTransfer,
        modifyContent,
        setCurrentlyEditing,
        selectionOverride,
      );
    },
    handleNewLineInsert: (selectionOverride?: DocSelection) => {
      return handleNewLineInsert(
        modifyContent,
        setCurrentlyEditing,
        selectionOverride,
      );
    },
    handleNewListInsert: (listType: "regularList" | "numberedList", selectionOverride?: DocSelection) => {
      return handleNewListInsert(
        listType,
        modifyContent,
        setCurrentlyEditing,
        selectionOverride,
      );
    },
    handleNewMediaInsert: (mediaType: MediaType, currentDocument: CurrentDocument, selectionOverride?: DocSelection) => {
      return handleNewMediaInsert(
        mediaType,
        currentDocument,
        modifyContent,
        setCurrentlyEditing,
        selectionOverride,
      );
    },
    handleDelete: (selectionOverride?: DocSelection) => {
      return handleDelete(
        modifyContent,
        setCurrentlyEditing,
        selectionOverride,
      );
    },
    handleStyle: (tagName: StyleType, selectionOverride?: DocSelection) => {
      return handleStyle(
        tagName,
        modifyContent,
        setCurrentlyEditing,
        selectionOverride,
      );
    },
  };
}


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface StateItem {
  id: number;
  state: State;
  startRetry?: boolean;
}

export function generateStateActions<Item extends StateItem, ItemLocalData extends object>(
  dispatch: Dispatch,
  actions: ReturnType<typeof generateItemActions<Item, ItemLocalData>>,
  getItemFromStore: (store: GlobalStore) => (id: ItemId) => ItemFromStore<Item, ItemLocalData>,
  id: ItemId,
): ItemStateActions {
  return {
    addState: (state: StrippedState) => {
      dispatch(
        ((dispatch: Dispatch, getState: () => GlobalStore) => {
          const newState = R.mapObjIndexed(R.mapObjIndexed((value) => ({value: value, updatedAt: Date.now()})), state);

          const stateModel = getItemFromStore(getState())(id);
          const editState = stateModel?._editData?.state || {};

          actions.setEditFields({state: R.mergeDeepRight(editState, newState)} as Partial<Item>);
          actions.update();
        }) as unknown as AnyAction,
      );
    },
    resetState: () => {
      actions.setEditFields({startRetry: true} as Partial<Item>);
      actions.update();
    },
  };
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// TODO: EXTRACT FROM FILE
function snakeCaseObjectKeys<T>(obj: Record<string, T>) {
  return Object.keys(obj).reduce((newObject, key) => {
    const snakeCasedKey = key.split(/(?=[A-Z])/).join("_").toLowerCase();

    if ((newObject)[snakeCasedKey] === undefined) {
      const value = obj[key];

      (newObject)[snakeCasedKey] = value;
    }

    return newObject;
  }, {} as Record<string, T>);
}

function allValuesToString(object: Record<string, unknown>) {
  return R.map((num) => String(num), object);
}

function listDataFromStore<Item extends {id: number}>(listStore: ItemListState<Item>=DEFAULT_LIST_STATE) {
  const data = R.pick([
      "search",
      "sortOrder",
      "sortBy",
      "page",
      "perPage",
    ] as const,
    listStore,
  );

  if (listStore.activeFilters && Object.values(listStore.activeFilters).length > 0) {
    return {
      ...data,
      where: JSON.stringify(Object.values(listStore.activeFilters)),
    };
  } else {
    return data;
  }
}

function readListPageWithData(itemType: ItemType, listID: string, endpoint: string, data: ReturnType<typeof listDataFromStore>) {
  return {
    type: READ_PAGE,
    meta: {itemType, listID},
    payload: fetchForReduxPromiseMiddleware(`/${endpoint}?${new URLSearchParams(allValuesToString(snakeCaseObjectKeys(data)))}`),
  };
}

function readListPage(itemType: ItemType, endpoint: string, listID: string) {
  return (dispatch: Dispatch, getState: () => GlobalStore) => {
    const list = rawListFromStore(getState(), itemType, listID);
    const data = listDataFromStore(list);

    dispatch(readListPageWithData(itemType, listID, endpoint, data));
  };
}

// TODO: Combine with readListPageWithData
function reloadListWithData(itemType: ItemType, listID: string, endpoint: string, data: ReturnType<typeof listDataFromStore>) {
  return {
    type: RELOAD_LIST,
    meta: {itemType, listID},
    payload: fetchForReduxPromiseMiddleware(`/${endpoint}?${new URLSearchParams(allValuesToString(snakeCaseObjectKeys(data)))}`),
  };
}

function reloadList(itemType: ItemType, endpoint: string, listID: string) {
  return (dispatch: Dispatch, getState: () => GlobalStore) => {
    const list = rawListFromStore(getState(), itemType, listID);
    let data = listDataFromStore(list);

    data = {
      ...data,
      page: 0,
      perPage: list.perPage * list.page,
    };

    dispatch(reloadListWithData(itemType, listID, endpoint, data));
  };
}

function resetListPages(itemType: ItemType, listID: string) {
  return {
    type: RESET_PAGES,
    meta: {itemType, listID},
  };
}

function addListFilter(itemType: ItemType, listID: string, filterID: string, filter: Filter) {
  return {
    type: ADD_FILTER,
    meta: {itemType, listID},
    payload: {filterID, filter},
  };
}

function removeListFilter(itemType: ItemType, listID: string, filterID: string) {
  return {
    type: REMOVE_FILTER,
    meta: {itemType, listID},
    payload: {filterID},
  };
}

function setListSearch(itemType: ItemType, listID: string, search: string) {
  return {
    type: SET_SEARCH,
    meta: {itemType, listID},
    payload: {search},
  };
}

function resetListSearch(itemType: ItemType, listID: string) {
  return {
    type: RESET_SEARCH,
    meta: {itemType, listID},
  };
}

function setListSort<Item extends {id: number}>(itemType: ItemType, listID: string, sortBy: keyof Item, sortOrder: SortOrder) {
  return {
    type: SET_SORT,
    meta: {itemType, listID},
    payload: {sortBy, sortOrder},
  };
}

function resetListSort(itemType: ItemType, listID: string) {
  return {
    type: RESET_SORT,
    meta: {itemType, listID},
  };
}

function setListSelected(itemType: ItemType, listID: string, selected: Array<ItemId>) {
  return {
    type: SET_SELECTED,
    meta: {itemType, listID},
    payload: {selected},
  };
}

function resetListSelected(itemType: ItemType, listID: string) {
  return {
    type: RESET_SELECTED,
    meta: {itemType, listID},
  };
}

function addListSelected(itemType: ItemType, listID: string, addSelected: Array<ItemId>) {
  return {
    type: ADD_SELECTED,
    meta: {itemType, listID},
    payload: {addSelected},
  };
}

function removeListSelected(itemType: ItemType, listID: string, removeSelected: Array<ItemId>) {
  return {
    type: REMOVE_SELECTED,
    meta: {itemType, listID},
    payload: {removeSelected},
  };
}

function setListItemsPerPage(itemType: ItemType, listID: string, perPage: number) {
  return {
    type: SET_ITEMS_PER_PAGE,
    meta: {itemType, listID},
    payload: {perPage},
  };
}

function generateListActions<Item extends {id: number}>(dispatch: Dispatch, itemType: ItemType, endpoint: string, listId: string) {
  return {
    readPage: () => dispatch(readListPage(itemType, endpoint, listId) as unknown as AnyAction),
    reload: () => dispatch(reloadList(itemType, endpoint, listId) as unknown as AnyAction),
    resetPages: () => dispatch(resetListPages(itemType, listId)),

    addFilter: (filterID: string, filter: Filter) => dispatch(addListFilter(itemType, listId, filterID, filter)),
    removeFilter: (filterID: string) => dispatch(removeListFilter(itemType, listId, filterID)),

    setSearch: (search: string) => dispatch(setListSearch(itemType, listId, search)),
    resetSearch: () => dispatch(resetListSearch(itemType, listId)),

    setSort: (sortBy: keyof Item, sortOrder: SortOrder) => dispatch(setListSort(itemType, listId, sortBy, sortOrder)),
    resetSort: () => dispatch(resetListSort(itemType, listId)),

    setSelected: (selected: Array<ItemId>) => dispatch(setListSelected(itemType, listId, selected)),
    resetSelected: () => dispatch(resetListSelected(itemType, listId)),

    addSelected: (addSelected: Array<ItemId>) => dispatch(addListSelected(itemType, listId, addSelected)),
    removeSelected: (removeSelected: Array<ItemId>) => dispatch(removeListSelected(itemType, listId, removeSelected)),

    setItemsPerPage: (perPage: number) => dispatch(setListItemsPerPage(itemType, listId, perPage)),
  };
}

////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////

export function itemListAndActionsFromStore<Item extends {id: number}, ItemLocalData extends object, ItemExtraActions extends object>(
  dispatch: Dispatch,
  store: GlobalStore,
  itemType: ItemType,
  itemAndActionsFromStore: (dispatch: Dispatch, store: GlobalStore) => (id: ItemId) => ItemAndActionsFromStore<Item, ItemLocalData, ItemExtraActions>,
  listId: string,
  listEndpoint: string,
) {
  const rawList = rawListFromStore<Item, ItemLocalData>(store, itemType, listId);
  const actions = generateListActions<Item>(dispatch, itemType, listEndpoint, listId);

  const rawListWithActions = {
    ...rawList,
    _actions: actions,
  };

  if ("listData" in rawListWithActions) {
    return {
      ...rawListWithActions,
      listData: rawListWithActions.listData.map((itemId) => itemAndActionsFromStore(dispatch, store)(itemId)),
      selected: rawListWithActions.selected.map((itemId) => itemAndActionsFromStore(dispatch, store)(itemId)),
    };
  } else {
    return rawListWithActions;
  }
}

export function generateItemListAndActionsFromStore<Item extends {id: number}, ItemLocalData extends object, ItemExtraActions extends object>(
  itemType: ItemType,
  itemAndActionsFromStore: (dispatch: Dispatch, store: GlobalStore) => (id: ItemId) => ItemAndActionsFromStore<Item, ItemLocalData, ItemExtraActions>,
) {
  return (dispatch: Dispatch, store: GlobalStore) => (listId: string, listEndpoint: string) => itemListAndActionsFromStore(dispatch, store, itemType, itemAndActionsFromStore, listId, listEndpoint);
}
